import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowUp, ArrowDown, Pencil, ExternalLink } from 'lucide-react';
import BannerImageUploader from '@/components/admin/BannerImageUploader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });
const selectClass = 'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const AdminBannersPage = () => {
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState({});
  const [editing, setEditing] = useState(null); // banner being edited
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchBanners = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/banners`, getAuthHeaders());
      setBanners(res.data || []);
    } catch (e) {
      toast.error('Failed to load banners');
    }
  }, []);

  useEffect(() => {
    fetchBanners();
    axios.get(`${API}/categories`).then((r) => setCategories(r.data.categories || [])).catch(() => {});
    axios.get(`${API}/brands`).then((r) => setBrands(r.data.brands || {})).catch(() => {});
  }, [fetchBanners]);

  const move = async (index, dir) => {
    const next = [...banners];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setBanners(next);
    try {
      await axios.post(`${API}/admin/banners/reorder`, { ids: next.map((b) => b.id) }, getAuthHeaders());
      toast.success('Order updated');
    } catch (e) {
      toast.error('Failed to reorder');
      fetchBanners();
    }
  };

  const toggleActive = async (banner) => {
    try {
      await axios.put(`${API}/admin/banners/${banner.id}`, { active: !banner.active }, getAuthHeaders());
      setBanners((prev) => prev.map((b) => (b.id === banner.id ? { ...b, active: !b.active } : b)));
      toast.success(!banner.active ? 'Banner enabled' : 'Banner hidden');
    } catch (e) {
      toast.error('Failed to update banner');
    }
  };

  const openEdit = (banner) => {
    setEditing(banner);
    setForm({
      cta: banner.cta || '',
      linkType: banner.linkType || (banner.ctaLink?.startsWith('/products') ? 'filter' : 'custom'),
      linkCategory: banner.linkCategory || '',
      linkBrand: banner.linkBrand || '',
      customLink: banner.linkType === 'custom' ? banner.ctaLink : (banner.ctaLink?.startsWith('/products') ? '' : banner.ctaLink) || '',
      title: banner.title || '',
      image: banner.image || ''
    });
  };

  const computedLink = form.linkType === 'filter'
    ? `/products${form.linkCategory ? `?category=${form.linkCategory}` : ''}${form.linkBrand ? `${form.linkCategory ? '&' : '?'}brand=${encodeURIComponent(form.linkBrand)}` : ''}`
    : form.customLink;

  const saveEdit = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/banners/${editing.id}`, {
        cta: form.cta,
        title: form.title,
        image: form.image,
        linkType: form.linkType,
        linkCategory: form.linkType === 'filter' ? form.linkCategory : '',
        linkBrand: form.linkType === 'filter' ? form.linkBrand : '',
        ctaLink: computedLink || '/products'
      }, getAuthHeaders());
      toast.success('Banner updated');
      setEditing(null);
      fetchBanners();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const brandOptions = form.linkCategory ? (brands[form.linkCategory] || []) : [...new Set(Object.values(brands).flat())];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <h1 className="text-3xl font-outfit font-bold text-slate-900 mb-1">Banner Management</h1>
        <p className="text-sm text-slate-500 mb-8">Reorder homepage banners, edit destination links and configure category/brand filters. Design stays unchanged.</p>

        <div className="space-y-3">
          {banners.map((banner, index) => (
            <div key={banner.id} className={`bg-white border rounded-lg p-3 flex items-center gap-4 ${banner.active ? 'border-slate-200' : 'border-slate-200 opacity-60'}`} data-testid={`banner-row-${banner.id}`}>
              {/* Reorder */}
              <div className="flex flex-col gap-1">
                <Button variant="outline" size="icon" className="h-7 w-7" disabled={index === 0} onClick={() => move(index, -1)} data-testid={`banner-up-${banner.id}`}>
                  <ArrowUp className="w-3.5 h-3.5" />
                </Button>
                <Button variant="outline" size="icon" className="h-7 w-7" disabled={index === banners.length - 1} onClick={() => move(index, 1)} data-testid={`banner-down-${banner.id}`}>
                  <ArrowDown className="w-3.5 h-3.5" />
                </Button>
              </div>

              {/* Thumb */}
              <img src={banner.image} alt={banner.title} className="w-28 h-16 object-cover rounded-md border border-slate-100 shrink-0" />

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{banner.type}</span>
                  <h3 className="font-semibold text-slate-900 truncate">{banner.title}</h3>
                </div>
                <p className="text-xs text-slate-500 mt-1 flex items-center gap-1 truncate">
                  <ExternalLink className="w-3 h-3 shrink-0" />
                  <span className="font-mono truncate" data-testid={`banner-link-${banner.id}`}>{banner.ctaLink}</span>
                </p>
                {banner.linkType === 'filter' && (
                  <p className="text-xs text-slate-400 mt-0.5">
                    Filters: {banner.linkCategory || 'all categories'}{banner.linkBrand ? ` + ${banner.linkBrand}` : ''}
                  </p>
                )}
              </div>

              {/* Actions */}
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                <input type="checkbox" checked={!!banner.active} onChange={() => toggleActive(banner)} className="w-4 h-4 accent-[#0047AB]" data-testid={`banner-active-${banner.id}`} />
                Visible
              </label>
              <Button variant="outline" size="sm" onClick={() => openEdit(banner)} data-testid={`banner-edit-${banner.id}`}>
                <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
              </Button>
            </div>
          ))}
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Banner — {editing?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="banner-form-title" />
            </div>
            <div>
              <Label>Button Label (CTA)</Label>
              <Input value={form.cta || ''} onChange={(e) => setForm({ ...form, cta: e.target.value })} className="mt-1" data-testid="banner-form-cta" />
            </div>
            <div>
              <BannerImageUploader
                value={form.image || ''}
                onChange={(url) => setForm((f) => ({ ...f, image: url }))}
              />
            </div>
            <div>
              <Label>Destination</Label>
              <select
                value={form.linkType || 'filter'}
                onChange={(e) => setForm({ ...form, linkType: e.target.value })}
                className={`${selectClass} mt-1`}
                data-testid="banner-form-linktype"
              >
                <option value="filter">Products page with filters</option>
                <option value="custom">Custom link</option>
              </select>
            </div>

            {form.linkType === 'filter' ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category filter</Label>
                  <select
                    value={form.linkCategory || ''}
                    onChange={(e) => setForm({ ...form, linkCategory: e.target.value, linkBrand: '' })}
                    className={`${selectClass} mt-1`}
                    data-testid="banner-form-category"
                  >
                    <option value="">All categories</option>
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Brand filter</Label>
                  <select
                    value={form.linkBrand || ''}
                    onChange={(e) => setForm({ ...form, linkBrand: e.target.value })}
                    className={`${selectClass} mt-1`}
                    data-testid="banner-form-brand"
                  >
                    <option value="">No brand filter</option>
                    {brandOptions.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
              </div>
            ) : (
              <div>
                <Label>Custom URL / path</Label>
                <Input
                  value={form.customLink || ''}
                  onChange={(e) => setForm({ ...form, customLink: e.target.value })}
                  placeholder="/request-item"
                  className="mt-1"
                  data-testid="banner-form-customlink"
                />
              </div>
            )}

            <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 text-xs">
              <span className="text-slate-500">Banner will open: </span>
              <span className="font-mono text-[#0047AB]" data-testid="banner-form-preview">{computedLink || '—'}</span>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button className="bg-[#0047AB] hover:bg-[#003380]" disabled={saving} onClick={saveEdit} data-testid="banner-form-save">
                {saving ? 'Saving…' : 'Save Banner'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBannersPage;
